/**
 * The association's documents library and profile (arqam doc 52): labels and
 * the few rules the screens show. arqam numbers letters and keeps the
 * statutes in force; here only dates are compared, for badges.
 */

export const DOCUMENT_TYPES = [
  'STATUTES',
  'INTERNAL_RULES',
  'LEGAL',
  'PERMIT',
  'GA_MINUTES',
  'BOARD_MINUTES',
  'MORAL_REPORT',
  'FINANCIAL_REPORT',
  'CORRESPONDENCE_IN',
  'CORRESPONDENCE_OUT',
  'AGREEMENT',
  'CONTRACT',
  'TECHNICAL',
  'OTHER',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  STATUTES: 'القانون الأساسي',
  INTERNAL_RULES: 'النظام الداخلي',
  LEGAL: 'وثائق قانونية',
  PERMIT: 'رخص',
  GA_MINUTES: 'محاضر الجمع العام',
  BOARD_MINUTES: 'محاضر المكتب',
  MORAL_REPORT: 'التقارير الأدبية',
  FINANCIAL_REPORT: 'التقارير المالية',
  CORRESPONDENCE_IN: 'الوارد',
  CORRESPONDENCE_OUT: 'الصادر',
  AGREEMENT: 'اتفاقيات وشراكات',
  CONTRACT: 'عقود وعروض أثمان',
  TECHNICAL: 'وثائق تقنية',
  OTHER: 'أخرى',
};

/** Letters: numbered «N/YYYY» by arqam when left empty, replies expected. */
export const isCorrespondence = (type: string | null | undefined) =>
  type === 'CORRESPONDENCE_IN' || type === 'CORRESPONDENCE_OUT';

/** The other direction: a reply to a letter received is a letter sent. */
export const replyDirection = (type: DocumentType): DocumentType | null =>
  type === 'CORRESPONDENCE_IN'
    ? 'CORRESPONDENCE_OUT'
    : type === 'CORRESPONDENCE_OUT'
      ? 'CORRESPONDENCE_IN'
      : null;

/** «ينتظر الرد»: the date for a reply has passed and none is linked. */
export const awaitingReply = (
  doc: { replyDueOn: string | null; repliedById: string | null },
  today: string,
) => !!doc.replyDueOn && !doc.repliedById && doc.replyDueOn < today;

/** Days from `from` to `to` (dates "YYYY-MM-DD"), negative when past. */
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);

/**
 * The committee's term (ولاية المكتب), as the weekly report says it: from 90
 * days before its end, then once past. null otherwise.
 */
export function mandateNotice(
  mandateEnd: string | null | undefined,
  today: string,
): { past: boolean; days: number; text: string } | null {
  if (!mandateEnd) return null;
  const days = daysBetween(today, mandateEnd);
  if (days > 90) return null;
  return days < 0
    ? {
        past: true,
        days: -days,
        text: 'انتهت ولاية المكتب: يجب عقد الجمع العام لتجديد المكتب.',
      }
    : {
        past: false,
        days,
        text: 'تقترب نهاية ولاية المكتب: يجب التحضير للجمع العام.',
      };
}

/** A file's link that saves it instead of opening it (arqam `/d/…`). */
export function downloadUrl(url: string) {
  const u = new URL(url);
  u.searchParams.set('download', '1');
  return u.toString();
}
